/**
 * Smart Home AI — admin-only module
 *
 * Tabs: Dashboard | Devices | Alerts | Analytics | AI Assistant | Integrations | Settings
 *
 * Phase 1 Climate and Phase 2 Energy Intelligence are live.
 * The dashboard is organized by expandable home domains.
 */

import React, { useState, useEffect, useCallback } from "react";
import { homeAiApi, notificationApi } from "../services/api";
import { useToastActions } from "../components/shared/Toast";

// ── constants ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: "dashboard",    label: "Dashboard",    icon: "⬡" },
  { id: "devices",      label: "Devices",      icon: "◎" },
  { id: "alerts",       label: "Alerts",       icon: "◉" },
  { id: "analytics",    label: "Analytics",    icon: "▦" },
  { id: "ai",           label: "AI Assistant", icon: "✦" },
  { id: "integrations", label: "Integrations", icon: "⊕" },
  { id: "settings",     label: "Settings",     icon: "⚙" },
];

const BRAND_LABELS = {
  nest: "Nest",
  ecobee: "Ecobee",
  sense: "Sense",
  solar_edge: "SolarEdge",
  enphase: "Enphase",
  ring: "Ring",
  home_assistant: "Home Assistant",
  smartthings: "SmartThings",
};

const HVAC_STATE_LABEL = {
  cooling:  "Cooling",
  heating:  "Heating",
  idle:     "Idle",
  "fan-only": "Fan only",
};

const HVAC_STATE_COLOR = {
  cooling:  "var(--hm-smart-cooling)",
  heating:  "var(--hm-smart-heating)",
  idle:     "var(--hm-muted)",
  "fan-only": "var(--hm-text-secondary)",
};

const SEVERITY_COLOR = {
  info:     "var(--hm-smart-info)",
  warning:  "var(--hm-smart-warning)",
  critical: "var(--hm-smart-critical)",
};

// ── small helpers ─────────────────────────────────────────────────────────────

function StatPill({ label, value, unit, color }) {
  return (
    <div className="hm-smart-stat-pill">
      <span className="hm-smart-stat-label">{label}</span>
      <span className="hm-smart-stat-value" style={color ? { color } : {}}>
        {value !== null && value !== undefined ? value : "—"}
        {unit && <span className="hm-smart-stat-unit"> {unit}</span>}
      </span>
    </div>
  );
}

// ── Thermostat card ───────────────────────────────────────────────────────────

function relativeSyncTime(value) {
  if (!value) return "Never";
  const elapsed = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours} hr ago` : `${Math.floor(hours / 24)} d ago`;
}

function ThermostatCard({ device, weather, intelligence, onPoll, polling }) {
  const state = device.last_state || {};
  const hvacState = (state.hvac_state || "idle").toLowerCase();
  const hvacMode  = (state.hvac_mode  || "").toLowerCase();
  const tempF     = state.temperature_f;
  const targetF   = state.target_temp_f;
  const humidity  = state.humidity_pct;
  const persistedCycleStart = state.hvac_cycle_started_at
    ? new Date(state.hvac_cycle_started_at).getTime()
    : null;
  const snapshotRuntime = persistedCycleStart && Number.isFinite(persistedCycleStart)
    ? Math.max(0, (Date.now() - persistedCycleStart) / 60000)
    : null;
  const activeRuntime = intelligence?.telemetry?.continuous_runtime_min ?? snapshotRuntime;
  const expectation = intelligence?.telemetry?.performance_expectation;

  return (
    <div className={`hm-smart-thermo-card hm-smart-thermo-${hvacState}`}>
      <div className="hm-smart-thermo-head">
        <div className="hm-smart-thermo-name">
          <span className="hm-smart-device-dot"
            style={{ background: state.status === "online" ? "var(--hm-smart-online)" : "var(--hm-muted)" }} />
          {device.name}
          {device.location && (
            <span className="hm-smart-thermo-location">{device.location}</span>
          )}
        </div>
        <button
          className="hm-smart-poll-btn"
          onClick={() => onPoll(device.id)}
          disabled={polling}
          title="Refresh now"
        >
          {polling ? "…" : "↺"}
        </button>
      </div>

      <div className="hm-smart-thermo-temps">
        <div className="hm-smart-thermo-ambient">
          {tempF !== null && tempF !== undefined ? `${tempF}°` : "—"}
          <span className="hm-smart-thermo-unit">F</span>
        </div>
        <div className="hm-smart-thermo-meta">
          {targetF !== null && targetF !== undefined && (
            <div className="hm-smart-thermo-target">
              Set to {targetF}°F
            </div>
          )}
          <div
            className="hm-smart-thermo-state"
            style={{ color: HVAC_STATE_COLOR[hvacState] || "var(--hm-muted)" }}
          >
            {HVAC_STATE_LABEL[hvacState] || hvacState}
          </div>
          {hvacMode && (
            <div className="hm-smart-thermo-mode">
              Mode: {hvacMode}
            </div>
          )}
        </div>
      </div>

      <div className="hm-smart-thermo-stats">
        {weather?.available && (
          <>
            <StatPill
              label="Outdoor"
              value={Math.round(weather.temperature_f)}
              unit="°F"
              color="var(--hm-smart-info)"
            />
            {tempF !== null && tempF !== undefined && (
              <StatPill
                label="Indoor gap"
                value={Math.abs(tempF - weather.temperature_f).toFixed(1)}
                unit="°F"
              />
            )}
          </>
        )}
        {humidity !== null && humidity !== undefined && (
          <StatPill label="Humidity" value={Math.round(humidity)} unit="%" />
        )}
        {hvacState === "cooling" || hvacState === "heating" ? (
          <>
            <StatPill
              label="Active runtime"
              value={activeRuntime == null ? "Tracking…" : activeRuntime < 1 ? "<1" : Math.round(activeRuntime)}
              unit={activeRuntime == null ? null : "min"}
              color={HVAC_STATE_COLOR[hvacState]}
            />
            {expectation && (
              <StatPill
                label="Condition-adjusted goal"
                value={`${expectation.adjusted_delta_f}° / ${expectation.window_min}m`}
                color="var(--hm-primary)"
              />
            )}
          </>
        ) : null}
        {device.last_seen_at && (
          <div title={new Date(device.last_seen_at).toLocaleString()}>
            <StatPill label="Last synced" value={relativeSyncTime(device.last_seen_at)} />
          </div>
        )}
        <StatPill
          label="Status"
          value={state.status || "unknown"}
          color={state.status === "online" ? "var(--hm-smart-online)" : "var(--hm-smart-critical)"}
        />
      </div>
    </div>
  );
}

function HvacEquipmentLink({ device, onChanged }) {
  const toastActions = useToastActions();
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [specs, setSpecs] = useState({ capacity_tons: "", seer: "", power_kw: "", filter_runtime_hours: 200, filter_size: "", filter_merv: "", has_pets: false, service_interval_months: 12 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    homeAiApi.getDeviceEquipment(device.id).then(result => {
      setData(result);
      const equipment = result.confirmed_equipment;
      setSelectedId(equipment?.vault_item_id ? String(equipment.vault_item_id) : String(result.candidates?.[0]?.vault_item_id || ""));
      setSpecs({
        capacity_tons: equipment?.capacity_tons ?? "",
        seer: equipment?.seer ?? "",
        power_kw: equipment?.power_kw ?? "",
        filter_runtime_hours: equipment?.filter_runtime_hours ?? 200,
        filter_size: equipment?.filter_size ?? "",
        filter_merv: equipment?.filter_merv ?? "",
        has_pets: equipment?.has_pets ?? false,
        service_interval_months: equipment?.service_interval_months ?? 12,
      });
    }).catch(() => setData({ candidates: [], confirmed_equipment: null }));
  }, [device.id]);

  useEffect(() => { load(); }, [load]);
  const confirmed = data?.confirmed_equipment;

  const save = async () => {
    if (!selectedId) return;
    const toastId = toastActions?.addToast?.(`Linking ${device.name} to Home Vault…`, "loading", { persist: true });
    setBusy(true);
    try {
      const result = await homeAiApi.confirmDeviceEquipment(device.id, { vault_item_id: Number(selectedId), ...specs });
      setData(result);
      toastActions?.updateToast?.(toastId, "HVAC identity confirmed for HomeMind analysis", "success");
      onChanged?.();
    } catch (error) {
      toastActions?.updateToast?.(toastId, error.message || "HVAC link could not be saved", "error");
    } finally { setBusy(false); }
  };

  const unlink = async () => {
    const toastId = toastActions?.addToast?.(`Unlinking ${device.name}…`, "loading", { persist: true });
    setBusy(true);
    try {
      await homeAiApi.unlinkDeviceEquipment(device.id);
      toastActions?.updateToast?.(toastId, "HVAC equipment link removed", "success");
      load();
      onChanged?.();
    } catch (error) {
      toastActions?.updateToast?.(toastId, error.message || "HVAC link could not be removed", "error");
    } finally { setBusy(false); }
  };

  return (
    <section className={`hm-smart-equipment-link${confirmed ? " is-confirmed" : ""}`}>
      <div className="hm-smart-equipment-head">
        <div><small>Equipment identity</small><strong>{confirmed ? "Confirmed from Home Vault" : "Confirm the HVAC serving this zone"}</strong></div>
        {confirmed && <span>✓ Linked</span>}
      </div>
      {confirmed ? (
        <>
          <div className="hm-smart-equipment-identity">
            <strong>{confirmed.name}</strong>
            <span>{[confirmed.brand, confirmed.model].filter(Boolean).join(" · ") || "Model details not saved"}</span>
            <small>{confirmed.warranty_expiry ? `Warranty through ${new Date(`${confirmed.warranty_expiry}T12:00:00`).toLocaleDateString()}` : "Warranty date not saved"}</small>
          </div>
          <div className="hm-smart-equipment-specs">
            <label>Capacity <input inputMode="decimal" value={specs.capacity_tons} onChange={e => setSpecs(s => ({ ...s, capacity_tons: e.target.value }))} placeholder="e.g. 3.5" /><span>tons</span></label>
            <label>SEER <input inputMode="decimal" value={specs.seer} onChange={e => setSpecs(s => ({ ...s, seer: e.target.value }))} placeholder="e.g. 16" /></label>
            <label>Power <input inputMode="decimal" value={specs.power_kw} onChange={e => setSpecs(s => ({ ...s, power_kw: e.target.value }))} placeholder="optional" /><span>kW</span></label>
            <label>Filter life <input inputMode="numeric" value={specs.filter_runtime_hours} onChange={e => setSpecs(s => ({ ...s, filter_runtime_hours: e.target.value }))} /><span>hours</span></label>
            <label>Filter size <input value={specs.filter_size} onChange={e => setSpecs(s => ({ ...s, filter_size: e.target.value }))} placeholder="20×20×1" /></label>
            <label>MERV <input inputMode="numeric" value={specs.filter_merv} onChange={e => setSpecs(s => ({ ...s, filter_merv: e.target.value }))} placeholder="e.g. 11" /></label>
            <label>HVAC service <input inputMode="numeric" value={specs.service_interval_months} onChange={e => setSpecs(s => ({ ...s, service_interval_months: e.target.value }))} /><span>months</span></label>
          </div>
          <label className="hm-smart-equipment-check"><input type="checkbox" checked={specs.has_pets} onChange={e => setSpecs(s => ({ ...s, has_pets: e.target.checked }))} /> Home has pets (useful context for filter recommendations)</label>
          <p>HomeMind uses these confirmed specifications with runtime, outdoor weather, indoor progress, utility rate, equipment age, warranty, and service history.</p>
          <div className="hm-smart-equipment-actions"><button onClick={save} disabled={busy}>Save profile</button><button className="is-secondary" onClick={unlink} disabled={busy}>Change link</button></div>
        </>
      ) : data ? (
        data.candidates?.length ? (
          <>
            <p>HouseMind found likely HVAC records. Select the system that actually serves this thermostat; a suggestion is never treated as fact until you confirm it.</p>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)}>
              {data.candidates.map(item => <option key={item.vault_item_id} value={item.vault_item_id}>{item.name}{item.location ? ` — ${item.location}` : ""}{item.model ? ` · ${item.model}` : ""}</option>)}
            </select>
            <div className="hm-smart-equipment-specs">
              <label>Capacity <input inputMode="decimal" value={specs.capacity_tons} onChange={e => setSpecs(s => ({ ...s, capacity_tons: e.target.value }))} placeholder="e.g. 3.5" /><span>tons</span></label>
              <label>SEER <input inputMode="decimal" value={specs.seer} onChange={e => setSpecs(s => ({ ...s, seer: e.target.value }))} placeholder="e.g. 16" /></label>
              <label>Power <input inputMode="decimal" value={specs.power_kw} onChange={e => setSpecs(s => ({ ...s, power_kw: e.target.value }))} placeholder="optional" /><span>kW</span></label>
            </div>
            <button onClick={save} disabled={busy || !selectedId}>Confirm this equipment</button>
          </>
        ) : (
          <p>No HVAC appliance or warranty record was found. <a href="/app/home-vault">Add the system to Home Vault</a>, including its location, model, warranty, and service details, then return here to confirm it.</p>
        )
      ) : <p>Checking Home Vault for HVAC equipment…</p>}
    </section>
  );
}

// ── RuntimeChart (simple CSS bar chart) ───────────────────────────────────────

function RuntimeChart({ deviceId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!deviceId) return;
    setLoading(true);
    homeAiApi.runtimeSummary(deviceId, 7)
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [deviceId]);

  if (loading) return <div className="hm-smart-chart-loading">Loading runtime data…</div>;
  if (!data.length) return <div className="hm-smart-chart-empty">No runtime data yet. Check back after the first poll cycle.</div>;

  const max = Math.max(...data.map(d => d.runtime_min), 1);

  return (
    <div className="hm-smart-chart">
      <div className="hm-smart-chart-title">HVAC runtime — last 7 days</div>
      <div className="hm-smart-chart-bars">
        {data.map(d => {
          const pct = Math.round((d.runtime_min / max) * 100);
          const hrs = Math.floor(d.runtime_min / 60);
          const mins = Math.round(d.runtime_min % 60);
          const label = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
          const day = new Date(d.date + "T12:00:00").toLocaleDateString([], { weekday: "short" });
          return (
            <div key={d.date} className="hm-smart-chart-bar-wrap" title={`${day}: ${label}`}>
              <div className="hm-smart-chart-bar-track">
                <div className="hm-smart-chart-bar-fill" style={{ height: `${pct}%` }} />
              </div>
              <div className="hm-smart-chart-bar-label">{day}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Dashboard tab ─────────────────────────────────────────────────────────────

function RecommendationsPanel({ analysis, onOpenAi }) {
  const deviceAnalyses = analysis?.devices || [];
  if (!deviceAnalyses.length) return null;
  const recommendations = deviceAnalyses.flatMap(device =>
    (device.recommendations || []).map(item => ({ ...item, deviceName: device.device_name }))
  );
  const score = Math.min(...deviceAnalyses.map(device => device.health_score ?? 0));
  return (
    <section className="hm-smart-intelligence">
      <div className="hm-smart-intelligence-head">
        <div>
          <div className="hm-smart-section-label">🧠 Live HomeMind intelligence</div>
          <h3>{score} <small>/ 100 home health</small></h3>
        </div>
        <button onClick={onOpenAi}>Ask HomeMind ✦</button>
      </div>
      <div className="hm-smart-recommendation-grid">
        {recommendations.slice(0, 3).map((item, index) => (
          <article key={`${item.deviceName}-${item.category}-${index}`} className={`hm-smart-recommendation is-${item.level}`}>
            <div className="hm-smart-recommendation-meta">{item.deviceName} · {item.level}</div>
            <h4>{item.title}</h4>
            <p>{item.explanation}</p>
            <strong>{item.action}</strong>
            {item.suggested_setting && (
              <div className="hm-smart-setpoint">
                Suggested target: {item.suggested_setting.recommended_target_f}°F
                <small> Advisory only — HomeMind did not change your thermostat.</small>
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

const HOME_DOMAINS = [
  { id: "overview", icon: "⬡", label: "Home", sub: "All systems", status: "live" },
  { id: "climate", icon: "◉", label: "Climate", sub: "Comfort & HVAC", status: "live" },
  { id: "energy", icon: "⚡", label: "Energy", sub: "Phase 2 live", status: "live" },
  { id: "solar", icon: "☀", label: "Solar & battery", sub: "Bridge ready", status: "live" },
  { id: "maintenance", icon: "⌁", label: "Maintenance", sub: "Vault + runtime", status: "live" },
  { id: "appliances", icon: "▣", label: "Appliances", sub: "Vault lifecycle", status: "live" },
  { id: "safety", icon: "◇", label: "Safety", sub: "Vault lifecycle", status: "live" },
  { id: "security", icon: "⌂", label: "Security", sub: "Bridge ready", status: "live" },
  { id: "air-water", icon: "≈", label: "Air & water", sub: "Vault lifecycle", status: "live" },
];

function DomainNav({ active, onChange, devices, energy }) {
  const thermostatCount = devices.filter(device => device.device_type === "thermostat").length;
  const applianceCount = devices.filter(device => !["thermostat", "sensor", "inverter"].includes(device.device_type)).length;
  return (
    <div className="hm-smart-domain-rail" aria-label="Home systems">
      {HOME_DOMAINS.map(domain => {
        const detail = domain.id === "climate"
          ? `${thermostatCount} zone${thermostatCount === 1 ? "" : "s"}`
          : domain.id === "maintenance"
            ? `${energy?.summary?.thermostat_zones || thermostatCount} HVAC systems`
          : domain.id === "appliances"
            ? `${applianceCount} connected`
          : domain.id === "energy" && energy?.status === "active"
            ? `${energy.summary?.learned_zones || 0} learned`
            : domain.sub;
        return (
          <button
            key={domain.id}
            className={`hm-smart-domain-card${active === domain.id ? " is-active" : ""}${domain.status === "planned" ? " is-planned" : ""}`}
            onClick={() => domain.status === "live" && onChange(domain.id)}
            disabled={domain.status !== "live"}
          >
            <span className="hm-smart-domain-icon">{domain.icon}</span>
            <span><strong>{domain.label}</strong><small>{detail}</small></span>
            <em>{domain.status === "live" ? "Live" : domain.sub}</em>
          </button>
        );
      })}
    </div>
  );
}

function LifecycleIntelligence({ analysis, domain }) {
  const systems = analysis?.home_systems;
  if (!systems) return null;
  const keys = domain === "appliances" ? ["appliances"] : domain === "safety" ? ["safety", "structure"] : ["air", "water"];
  const items = keys.flatMap(key => systems.domains?.[key]?.items || []);
  const actions = keys.flatMap(key => systems.domains?.[key]?.actions || []);
  const labels = { appliances: "Appliance lifecycle", safety: "Safety & structure", "air-water": "Air & water" };
  return (
    <section className="hm-smart-domain-panel hm-smart-lifecycle-panel">
      <div className="hm-smart-domain-panel-head"><div><span>Home Vault intelligence</span><h3>{labels[domain]}</h3></div><a href="/app/home-vault">Open Home Vault →</a></div>
      <div className="hm-smart-lifecycle-summary"><strong>{items.length}</strong><span>identified records</span><strong>{actions.length}</strong><span>actions</span></div>
      {items.length ? <div className="hm-smart-lifecycle-grid">{items.map(item => (
        <article key={item.vault_item_id}><strong>{item.name}</strong><span>{[item.brand, item.model, item.location].filter(Boolean).join(" · ") || "Identity details incomplete"}</span><small>{item.age_years == null ? "Add purchase or installation date" : `${item.age_years} years old`} · Vault-monitored</small></article>
      ))}</div> : <div className="hm-smart-empty-copy"><div className="hm-smart-empty-title">No matching Home Vault records yet</div><p className="hm-smart-empty-desc">Add equipment identity, location, purchase date, warranty, and service history to activate lifecycle intelligence without a live device connection.</p></div>}
      {actions.length > 0 && <div className="hm-smart-maintenance-actions">{actions.map(action => <div key={`${action.vault_item_id}-${action.title}`} className={`is-${action.level}`}><span>{action.item_name}</span><strong>{action.title}</strong><p>{action.explanation}</p></div>)}</div>}
      <p className="hm-smart-energy-limit">{systems.limitations}</p>
    </section>
  );
}

function SolarBatteryPanel({ energy }) {
  const systems = energy?.electrical?.renewables || [];
  return <section className="hm-smart-domain-panel hm-smart-lifecycle-panel">
    <div className="hm-smart-domain-panel-head"><div><span>☀️ Phase 5 · Solar & battery</span><h3>Generation and storage</h3></div></div>
    {systems.length ? <div className="hm-smart-lifecycle-grid">{systems.map(system => <article key={system.device_id}><strong>{system.device_name}</strong><span>{system.status} · {system.device_type}</span><small>{system.power_w == null ? "Live power unavailable" : `${Math.round(system.power_w)} W now`}{system.energy_kwh == null ? "" : ` · ${system.energy_kwh} kWh`}{system.battery_pct == null ? "" : ` · ${system.battery_pct}% charged`}</small></article>)}</div> : <div className="hm-smart-empty-copy"><div className="hm-smart-empty-title">Ready for solar or battery data</div><p className="hm-smart-empty-desc">Add a manual/local inverter or battery in Devices now. SolarEdge, Enphase, and Powerwall cloud adapters still require vendor credentials.</p></div>}
    <p className="hm-smart-energy-limit">Values are measured only when provided by the inverter, battery, or local bridge. HomeMind does not infer solar production from weather alone.</p>
  </section>;
}

function SecurityPanel({ devices }) {
  const security = devices.filter(device => ["door_sensor", "smart_lock", "garage_door", "security_sensor"].includes(device.device_type));
  return <section className="hm-smart-domain-panel hm-smart-lifecycle-panel"><div className="hm-smart-domain-panel-head"><div><span>🔒 Phase 7 · Security events</span><h3>Doors, locks and garage</h3></div></div>
    {security.length ? <div className="hm-smart-lifecycle-grid">{security.map(device => { const state=device.last_state||{}; return <article key={device.id}><strong>{device.name}</strong><span>{device.location || device.device_type.replaceAll('_',' ')}</span><small>{state.door_state ? `Door ${state.door_state}` : state.switch_state ? `State ${state.switch_state}` : "Waiting for state"} · {state.status || "unknown"}</small></article>; })}</div> : <div className="hm-smart-empty-copy"><div className="hm-smart-empty-title">Ready for privacy-first security events</div><p className="hm-smart-empty-desc">Add a manual/local door, lock, garage, or security sensor. HomeMind stores state events only—no video.</p></div>}
    <p className="hm-smart-energy-limit">HomeMind does not store video or perform facial recognition. Live vendor events require a supported local bridge or future vendor adapter.</p></section>;
}

function RuntimeValue({ minutes }) {
  const value = Number(minutes || 0);
  const hours = Math.floor(value / 60);
  const mins = Math.round(value % 60);
  return <>{hours ? `${hours}h ${mins}m` : `${mins}m`}</>;
}

function HomeBrief({ analysis, energy, alerts }) {
  const devices = analysis?.devices || [];
  const score = devices.length ? Math.min(...devices.map(device => device.health_score ?? 0)) : null;
  const maintenance = analysis?.maintenance || {};
  const systems = analysis?.home_systems?.summary || {};
  const unread = alerts.filter(alert => !alert.is_read && !alert.is_dismissed).length;
  const primary = devices.flatMap(device => device.recommendations || [])[0];
  return (
    <section className="hm-smart-brief">
      <div><small>HomeMind brief</small><h3>{score == null ? "Your home is ready to learn" : `Home health ${score}/100`}</h3><p>{primary?.explanation || "No urgent climate issue is visible in the latest readings."}</p></div>
      <div className="hm-smart-brief-facts">
        <span><strong>{unread}</strong> unread alerts</span>
        <span><strong>{maintenance.action_count || 0}</strong> HVAC actions</span>
        <span><strong>{systems.identified_items || 0}</strong> Vault systems</span>
        <span><strong>{energy?.confidence?.level || "low"}</strong> energy confidence</span>
      </div>
    </section>
  );
}

function MaintenanceIntelligence({ analysis, compact = false, onChanged }) {
  const toastActions = useToastActions();
  const [busyKey, setBusyKey] = useState("");
  const devices = analysis?.devices || [];
  if (!devices.length) return null;
  const profiles = devices.map(device => ({ device, maintenance: device.telemetry?.maintenance }));
  const summary = analysis?.maintenance || {};
  const actions = profiles.flatMap(({ device, maintenance }) =>
    (maintenance?.actions || []).map(action => ({ ...action, deviceName: device.device_name, deviceId: device.device_id }))
  );
  const complete = async (deviceId, type, deviceName) => {
    const key = `${deviceId}:${type}`;
    const toastId = toastActions?.addToast?.(`Saving ${deviceName} maintenance…`, "loading", { persist: true });
    setBusyKey(key);
    try {
      await homeAiApi.completeDeviceMaintenance(deviceId, type);
      toastActions?.updateToast?.(toastId, type === "filter" ? "Filter replacement saved and runtime reset" : "HVAC service saved to Home Vault", "success");
      await onChanged?.();
    } catch (error) {
      toastActions?.updateToast?.(toastId, error.message || "Maintenance could not be saved", "error");
    } finally { setBusyKey(""); }
  };
  const dismissAction = async (action) => {
    const key = `${action.category || "maintenance"}:${action.title || ""}`.slice(0, 500);
    setBusyKey(`${action.deviceId}:dismiss`);
    try {
      await homeAiApi.dismissDeviceMaintenance(action.deviceId, key);
      await onChanged?.();
    } finally { setBusyKey(""); }
  };
  return (
    <section className="hm-smart-domain-panel hm-smart-maintenance-panel">
      <div className="hm-smart-domain-panel-head">
        <div><span>🔧 HVAC maintenance intelligence</span><h3>Equipment, coverage and service</h3></div>
        <a href="/app/home-vault">Open Home Vault →</a>
      </div>
      <div className="hm-smart-maintenance-metrics">
        <div><small>Systems identified</small><strong>{summary.confirmed_systems || 0}/{summary.total_systems || devices.length}</strong><em>User confirmed</em></div>
        <div><small>Maintenance actions</small><strong>{summary.action_count || 0}</strong><em>{summary.urgent_count ? `${summary.urgent_count} priority` : "Nothing urgent"}</em></div>
        {profiles.slice(0, compact ? 2 : profiles.length).map(({ device, maintenance }) => (
          <article key={device.device_id}>
            <div><strong>{device.device_name}</strong><span>{maintenance?.equipment?.name || "Equipment link needed"}</span></div>
            <p>
              Warranty {maintenance?.warranty?.status || "unknown"}
              {maintenance?.filter?.observed_runtime_hours != null ? ` · ${maintenance.filter.observed_runtime_hours} observed HVAC hours` : " · Runtime tracking pending"}
            </p>
            {maintenance?.equipment_confirmed && (
              <div className="hm-smart-maintenance-quick-actions">
                <button disabled={!!busyKey} onClick={() => complete(device.device_id, "filter", device.device_name)}>✓ Filter replaced</button>
                <button disabled={!!busyKey} onClick={() => complete(device.device_id, "service", device.device_name)}>✓ Service completed</button>
              </div>
            )}
            <small>{maintenance?.service?.next_service_date ? `Next service ${new Date(`${maintenance.service.next_service_date}T12:00:00`).toLocaleDateString()}` : "Add the next service date in Home Vault"}</small>
          </article>
        ))}
      </div>
      {actions.length > 0 ? (
        <div className="hm-smart-maintenance-actions">
          {actions.slice(0, compact ? 2 : 6).map((action, index) => (
            <div key={`${action.deviceName}-${action.category}-${index}`} className={`is-${action.level}`}>
              <button className="hm-smart-maintenance-dismiss" disabled={!!busyKey} onClick={() => dismissAction(action)} title="Clear this notice">×</button>
              <span>{action.deviceName}</span><strong>{action.title}</strong><p>{action.explanation}</p><small>{action.action}</small>
            </div>
          ))}
        </div>
      ) : <p className="hm-smart-energy-limit">No warranty, service, recall, filter-runtime, or equipment-profile action needs attention.</p>}
    </section>
  );
}

function ClimateOverview({ thermostats, weather, analysis, onOpen }) {
  return (
    <section className="hm-smart-domain-panel hm-smart-climate-overview">
      <div className="hm-smart-domain-panel-head">
        <div><span>🌤️ Climate</span><h3>Comfort across the home</h3></div>
        <button onClick={onOpen}>Open climate →</button>
      </div>
      <div className="hm-smart-climate-strip">
        {thermostats.map(device => {
          const state = device.last_state || {};
          const insight = analysis?.devices?.find(item => item.device_id === device.id);
          return (
            <article key={device.id}>
              <div><i className={state.status === "online" ? "is-online" : ""} /><strong>{device.name}</strong></div>
              <span>{state.temperature_f != null ? `${state.temperature_f}°` : "—"}</span>
              <small>{HVAC_STATE_LABEL[(state.hvac_state || "idle").toLowerCase()] || "Idle"} · {Math.round(insight?.telemetry?.continuous_runtime_min || 0)} min</small>
            </article>
          );
        })}
        {weather?.available && (
          <article className="is-outdoor">
            <div><strong>Outdoor load</strong></div>
            <span>{Math.round(weather.temperature_f)}°</span>
            <small>{weather.location || "Current conditions"}</small>
          </article>
        )}
      </div>
    </section>
  );
}

function ApplianceOverview({ devices, health, onPoll, pollingId, onOpenIntegrations }) {
  if (!devices.length) {
    return (
      <div className="hm-smart-empty hm-smart-empty-dashboard">
        <div className="hm-smart-empty-icon" aria-hidden="true">▣</div>
        <div className="hm-smart-empty-copy">
          <div className="hm-smart-empty-kicker">Appliance health</div>
          <div className="hm-smart-empty-title">Connect Samsung SmartThings</div>
          <p className="hm-smart-empty-desc">Bring compatible refrigerators, TVs, laundry, kitchen appliances, and sensors into HomeMind.</p>
        </div>
        <button className="hm-smart-empty-action" onClick={onOpenIntegrations}>Open integrations <span>→</span></button>
      </div>
    );
  }
  return (
    <section className="hm-smart-domain-panel hm-smart-appliance-panel">
      <div className="hm-smart-domain-panel-head"><div><span>🔌 Phase 3 · Appliance monitoring</span><h3>Your connected appliances</h3></div></div>
      <div className="hm-smart-appliance-grid">
        {devices.map(device => {
          const state = device.last_state || {};
          const assessment = health?.data?.find(item => item.device_id === device.id);
          const detail = state.operating_state || state.media_state || state.switch_state || "Monitoring";
          const signals = [
            state.temperature_f != null ? `${state.temperature_f}°F` : null,
            state.door_state ? `Door ${state.door_state}` : null,
            state.power_w != null ? `${Math.round(state.power_w)} W` : null,
            state.energy_kwh != null ? `${state.energy_kwh} kWh` : null,
          ].filter(Boolean);
          return (
            <article key={device.id}>
              <div><i className={state.status === "online" ? "is-online" : ""} /><strong>{device.name}</strong><span>{BRAND_LABELS[device.brand] || device.brand}</span></div>
              <h4>{detail}</h4>
              <p>{signals.join(" · ") || `${device.device_type.replaceAll("_", " ")} · ${state.status || "unknown"}`}</p>
              {assessment && <div className={`hm-smart-health-badge is-${assessment.health}`}><strong>{assessment.health_score}/100 · {assessment.health}</strong><span>{assessment.reasons[0]} · {assessment.confidence} confidence</span></div>}
              <button onClick={() => onPoll(device.id)} disabled={pollingId === device.id}>{pollingId === device.id ? "Refreshing…" : "Refresh"}</button>
            </article>
          );
        })}
      </div>
      <p className="hm-smart-energy-limit">Read-only monitoring. HomeMind records only the capabilities each device exposes.</p>
    </section>
  );
}

function EnergyIntelligence({ energy, onOpen }) {
  if (!energy) return null;
  const summary = energy.summary || {};
  const primary = energy.recommendations?.[0];
  const electricity = energy.electricity || {};
  const rateLabel = electricity.rate_per_kwh == null
    ? "Add bill amount + kWh in Utilities"
    : `${electricity.provider || "Saved electric bills"} · ${(electricity.rate_per_kwh * 100).toFixed(1)}¢/kWh`;
  const costBasis = electricity.estimated_hvac_cost == null
    ? (electricity.estimated_zones ? "Electric rate unavailable" : "Confirm HVAC power profile")
    : `Estimated today · ${rateLabel}`;
  const forecast = energy.forecast || {};
  const peak = energy.peak_runtime || {};
  const costHistory = electricity.daily_history || [];
  const electrical = energy.electrical || {};
  const maxDailyCost = Math.max(...costHistory.map(row => row.estimated_cost || 0), 0.01);
  return (
    <section className="hm-smart-domain-panel hm-smart-energy-panel">
      <div className="hm-smart-domain-panel-head">
        <div><span>⚡ Phase 2 · Energy intelligence · {energy.confidence?.level || "low"} confidence</span><h3>Demand, explained by conditions</h3></div>
        {onOpen && <button onClick={onOpen}>Open energy →</button>}
      </div>
      <div className="hm-smart-energy-metrics">
        <div><small>HVAC runtime today</small><strong><RuntimeValue minutes={summary.today_runtime_min} /></strong><em>Measured</em></div>
        <div><small>30-day daily baseline</small><strong>{summary.daily_average_min == null ? "Learning" : <RuntimeValue minutes={summary.daily_average_min} />}</strong><em>All climate zones</em></div>
        <div><small>Weather-matched zones</small><strong>{summary.learned_zones || 0}/{summary.thermostat_zones || 0}</strong><em>{summary.learning_zones ? "Still learning" : "Baseline ready"}</em></div>
        <div title={electricity.rate_per_kwh != null ? "Uses your saved electric rate or the effective rate calculated from bill total divided by recorded kWh." : "Add an electric bill with both its total and kWh usage, or save the provider rate in Utilities."}>
          <small>Estimated cooling cost</small>
          <strong>{electricity.estimated_hvac_cost == null ? "Unavailable" : `$${electricity.estimated_hvac_cost}`}</strong>
          <em>{costBasis}</em>
        </div>
      </div>
      {primary && (
        <div className={`hm-smart-energy-signal is-${primary.level}`}>
          <span>✦</span><div><strong>{primary.title}</strong><p>{primary.message}</p><small>{primary.action}</small></div>
        </div>
      )}
      {!onOpen && (
        <>
          <div className="hm-smart-energy-forecast-grid">
            <article><small>Month-to-date HVAC</small><strong>{electricity.month_to_date_hvac_cost == null ? "Unavailable" : `$${electricity.month_to_date_hvac_cost}`}</strong><span>{electricity.month_to_date_hvac_kwh == null ? "Needs confirmed profiles" : `${electricity.month_to_date_hvac_kwh} estimated kWh`}</span></article>
            <article><small>Projected HVAC month</small><strong>{electricity.projected_month_hvac_cost == null ? "Learning" : `$${electricity.projected_month_hvac_cost}`}</strong><span>Observed-day pace, not whole-home bill</span></article>
            <article><small>Tomorrow’s cooling</small><strong>{forecast.estimated_hvac_cost == null ? "Learning" : `$${forecast.estimated_hvac_cost}`}</strong><span>{forecast.outdoor_high_f == null ? "Forecast unavailable" : `${Math.round(forecast.outdoor_high_f)}°F forecast high · ${Math.round(forecast.estimated_runtime_min || 0)} min`}</span></article>
            <article><small>Peak runtime period</small><strong>{peak.label || "Learning"}</strong><span>{peak.runtime_min ? `${Math.round(peak.runtime_min)} observed minutes over 30 days` : "More runtime history needed"}</span></article>
          </div>
          {costHistory.some(row => row.estimated_cost != null) && (
            <div className="hm-smart-energy-history">
              <div><strong>Daily HVAC cost estimate</strong><span>Last 30 days · runtime × confirmed equipment power × saved effective rate</span></div>
              <div className="hm-smart-energy-history-bars">
                {costHistory.slice(-14).map(row => (
                  <i key={row.date} title={`${new Date(`${row.date}T12:00:00`).toLocaleDateString()}: $${row.estimated_cost ?? 0}`} style={{ height: `${Math.max(((row.estimated_cost || 0) / maxDailyCost) * 100, 4)}%` }} />
                ))}
              </div>
            </div>
          )}
          <div className="hm-smart-section-label">🔌 Circuit & appliance power</div>
          {electrical.monitors?.length ? <div className="hm-smart-energy-zones">{electrical.monitors.map(monitor => (
            <article key={monitor.device_id}><div><strong>{monitor.device_name}</strong><span>{monitor.status}</span></div><p>{monitor.current_power_w == null ? "No current watts" : `${Math.round(monitor.current_power_w)} W now`}</p><small>{monitor.seven_day_average_power_w == null ? "Building a power baseline" : `${monitor.seven_day_average_power_w} W recent average`}</small><em>{monitor.energy_kwh == null ? "Cumulative kWh unavailable" : `${monitor.energy_kwh} kWh reported`}</em></article>
          ))}</div> : <p className="hm-smart-energy-limit">Add a manual/local power meter or connect supported monitoring hardware to unlock circuit and appliance power baselines.</p>}
          <p className="hm-smart-energy-limit">{electrical.limitations}</p>
          <div className="hm-smart-section-label">⏱️ Zone runtime intelligence</div>
          <div className="hm-smart-energy-zones">
            {(energy.zones || []).map(zone => (
              <article key={zone.device_id}>
                <div><strong>{zone.device_name}</strong><span>{zone.hvac_state}</span></div>
                <p><RuntimeValue minutes={zone.today_runtime_min} /> today</p>
                <small>{zone.deviation_pct == null ? "Collecting a 30-day baseline" : `${zone.deviation_pct > 0 ? "+" : ""}${zone.deviation_pct}% vs daily baseline`}</small>
                <em>{zone.similar_weather?.status === "ready" ? `${zone.similar_weather.matching_days} similar-weather days` : "Similar-weather baseline learning"}</em>
              </article>
            ))}
          </div>
          <p className="hm-smart-energy-limit">{energy.limitations}</p>
          {electricity.rate_per_kwh != null && (
            <p className="hm-smart-energy-limit">
              Rate basis: {rateLabel} from {electricity.rate_sample_count} saved bill{electricity.rate_sample_count === 1 ? "" : "s"}
              {electricity.rate_as_of ? `, latest ${new Date(`${electricity.rate_as_of}T12:00:00`).toLocaleDateString()}` : ""}. Bill total ÷ kWh is an effective rate and can include delivery charges and taxes.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function DashboardTab({ devices, alerts, weather, analysis, energy, applianceHealth, onPoll, pollingId, onOpenIntegrations, onOpenAi, onRefresh }) {
  const [domain, setDomain] = useState("overview");
  const thermostats = devices.filter(d => d.device_type === "thermostat");
  const appliances = devices.filter(d => !["thermostat", "sensor", "inverter"].includes(d.device_type));
  const unreadAlerts = alerts.filter(a => !a.is_read && !a.is_dismissed);
  const onlineDevices = devices.filter(d => d.last_state?.status === "online").length;

  return (
    <div className="hm-smart-dashboard">
      {/* Home health bar */}
      <div className="hm-smart-health-bar">
        <div className="hm-smart-health-copy">
          <div className="hm-smart-health-kicker">🏠 HomeMind overview</div>
          <div className="hm-smart-health-label">
            <span className="hm-smart-health-icon">⬡</span>
            Your home, one signal
          </div>
          <p>Live device health and the next best action, without the noise.</p>
        </div>
        <div className="hm-smart-health-pills">
          <span className="hm-smart-health-pill">
            <strong>{onlineDevices}</strong>
            <small>Online</small>
          </span>
          <span className="hm-smart-health-pill">
            <strong>{devices.length}</strong>
            <small>Devices</small>
          </span>
          <span className={`hm-smart-health-pill${unreadAlerts.length ? " hm-smart-health-pill-warn" : ""}`}>
            <strong>{unreadAlerts.length}</strong>
            <small>Alerts</small>
          </span>
        </div>
      </div>

      <DomainNav active={domain} onChange={setDomain} devices={devices} energy={energy} />

      {domain === "overview" && thermostats.length > 0 && (
        <>
          <HomeBrief analysis={analysis} energy={energy} alerts={alerts} />
          <ClimateOverview thermostats={thermostats} weather={weather} analysis={analysis} onOpen={() => setDomain("climate")} />
          <EnergyIntelligence energy={energy} onOpen={() => setDomain("energy")} />
          <MaintenanceIntelligence analysis={analysis} compact onChanged={onRefresh} />
        </>
      )}

      {(domain === "climate" || (domain === "overview" && thermostats.length === 0)) && (thermostats.length === 0 ? (
        <div className="hm-smart-empty hm-smart-empty-dashboard">
          <div className="hm-smart-empty-icon" aria-hidden="true">◎</div>
          <div className="hm-smart-empty-copy">
            <div className="hm-smart-empty-kicker">Ready when you are</div>
            <div className="hm-smart-empty-title">Connect your first thermostat</div>
            <p className="hm-smart-empty-desc">
              Bring Nest into HomeMind to see temperature, runtime, and home health here.
            </p>
          </div>
          <button className="hm-smart-empty-action" onClick={onOpenIntegrations}>
            Open integrations <span aria-hidden="true">→</span>
          </button>
        </div>
      ) : (
        <>
          <div className="hm-smart-section-label">🌡️ Thermostats</div>
          <div className="hm-smart-thermo-grid">
            {thermostats.map(d => (
              <div key={d.id} className="hm-smart-climate-zone">
                <ThermostatCard
                  device={d}
                  weather={weather}
                  intelligence={analysis?.devices?.find(item => item.device_id === d.id)}
                  onPoll={onPoll}
                  polling={pollingId === d.id}
                />
                <HvacEquipmentLink device={d} onChanged={onRefresh} />
              </div>
            ))}
          </div>
          {/* Runtime chart for first thermostat */}
          <RuntimeChart deviceId={thermostats[0]?.id} />
        </>
      ))}

      {domain === "energy" && <EnergyIntelligence energy={energy} />}
      {domain === "solar" && <SolarBatteryPanel energy={energy} />}
      {domain === "security" && <SecurityPanel devices={devices} />}

      {domain === "maintenance" && <MaintenanceIntelligence analysis={analysis} onChanged={onRefresh} />}

      {domain === "appliances" && appliances.length > 0 && <ApplianceOverview devices={appliances} health={applianceHealth} onPoll={onPoll} pollingId={pollingId} onOpenIntegrations={onOpenIntegrations} />}
      {["appliances", "safety", "air-water"].includes(domain) && <LifecycleIntelligence analysis={analysis} domain={domain} />}

      {domain === "climate" && <RecommendationsPanel analysis={analysis} onOpenAi={onOpenAi} />}

      {/* Recent alerts preview */}
      {unreadAlerts.length > 0 && (
        <>
          <div className="hm-smart-section-label">🔔 Recent alerts</div>
          <div className="hm-smart-alert-list">
            {unreadAlerts.slice(0, 3).map(a => (
              <div key={a.id} className="hm-smart-alert-row">
                <span
                  className="hm-smart-alert-dot"
                  style={{ background: SEVERITY_COLOR[a.severity] || "var(--hm-muted)" }}
                />
                <div className="hm-smart-alert-body">
                  <div className="hm-smart-alert-title">{a.title}</div>
                  {a.message && <div className="hm-smart-alert-msg">{a.message}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {domain === "overview" && <div className="hm-smart-roadmap-note"><strong>Built around your home.</strong><span>Climate, Energy and HVAC Maintenance use live telemetry. Appliances, safety, air and water now add Home Vault lifecycle intelligence while vendor telemetry remains optional.</span></div>}
    </div>
  );
}

function AiAssistantTab({ analysis }) {
  const [question, setQuestion] = useState("Explain my home's climate and energy demand today.");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ask = async (prompt = question) => {
    if (!prompt.trim()) return;
    setQuestion(prompt);
    setBusy(true);
    setError("");
    try {
      const response = await homeAiApi.ask(prompt, history);
      setResult(response);
      setHistory(current => [...current, { role: "user", text: prompt }, { role: "assistant", text: response.answer }].slice(-8));
    } catch (e) {
      setError(e.message || "HomeMind could not complete the analysis.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="hm-smart-ai">
      <div className="hm-smart-ai-hero">
        <div className="hm-smart-ai-kicker">✦ Home intelligence · advisory mode</div>
        <h2>Ask your home what it needs.</h2>
        <p>HomeMind combines climate zones, runtime history, temperature progress, humidity, outdoor load, and learned energy-demand patterns.</p>
        <div className="hm-smart-ai-prompts">
          {["Why is my AC running so long?", "How much did cooling cost yesterday?", "Which HVAC system needs maintenance?", "Are there any leak, air, or security concerns?", "What should I watch tomorrow?"].map(prompt => (
            <button key={prompt} onClick={() => ask(prompt)}>{prompt}</button>
          ))}
        </div>
        <div className="hm-smart-ai-compose">
          <textarea value={question} onChange={e => setQuestion(e.target.value)} rows="3" />
          <button onClick={() => ask()} disabled={busy || !question.trim()}>{busy ? "Analyzing…" : "Analyze home ✦"}</button>
        </div>
      </div>
      {error && <div className="hm-smart-ai-error">{error}</div>}
      {result && (
        <div className={`hm-smart-ai-answer is-${result.priority || "normal"}`}>
          <div className="hm-smart-ai-answer-meta">{result.source === "ai" ? "AI analysis" : "HomeMind monitoring analysis"} · {result.priority}</div>
          <h3>{result.answer}</h3>
          {(result.recommendations || []).map((item, index) => (
            <div className="hm-smart-ai-action" key={`${item.title}-${index}`}>
              <strong>{item.title}</strong><span>{item.reason}</span>
              {item.suggested_target_f != null && <em>Suggested target {item.suggested_target_f}°F</em>}
            </div>
          ))}
          {result.watch_next && <p><strong>Watching next:</strong> {result.watch_next}</p>}
          {result.limitations && <small>{result.limitations}</small>}
        </div>
      )}
      {!result && analysis?.devices?.length > 0 && (
        <RecommendationsPanel analysis={analysis} onOpenAi={() => ask()} />
      )}
    </div>
  );
}

// ── Devices tab ───────────────────────────────────────────────────────────────

function DevicesTab({ devices, onPoll, pollingId, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", brand: "manual", device_type: "air_quality_sensor", location: "" });
  const [snapshotDevice, setSnapshotDevice] = useState(null);
  const [bridgeInfo, setBridgeInfo] = useState(null);
  const [snapshot, setSnapshot] = useState({ temperature_f: "", humidity_pct: "", battery_pct: "", co2_ppm: "", pm25: "", leak_detected: false, smoke_detected: false, co_detected: false, door_state: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleAdd = async () => {
    setSaving(true);
    setError("");
    try {
      await homeAiApi.createDevice(form);
      setShowAdd(false);
      setForm({ name: "", brand: "manual", device_type: "air_quality_sensor", location: "" });
      onRefresh();
    } catch (e) {
      setError(e.message || "Failed to add device");
    } finally {
      setSaving(false);
    }
  };
  const saveSnapshot = async () => {
    setSaving(true); setError("");
    try { await homeAiApi.recordDeviceSnapshot(snapshotDevice.id, snapshot); setSnapshotDevice(null); onRefresh(); }
    catch (e) { setError(e.message || "Reading could not be saved"); }
    finally { setSaving(false); }
  };
  const createBridgeToken = async () => {
    setSaving(true); setError("");
    try { setBridgeInfo(await homeAiApi.createDeviceBridgeToken(snapshotDevice.id)); }
    catch (e) { setError(e.message || "Bridge token could not be created"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from Smart Home AI?`)) return;
    try {
      await homeAiApi.deleteDevice(id);
      onRefresh();
    } catch (e) {
      alert(e.message || "Failed to remove device");
    }
  };

  return (
    <div className="hm-smart-devices">
      <div className="hm-smart-devices-head">
        <div className="hm-smart-section-label" style={{ margin: 0 }}>📱 Registered devices</div>
        <button className="hm-smart-add-btn" onClick={() => setShowAdd(v => !v)}>
          {showAdd ? "Cancel" : "+ Add device"}
        </button>
      </div>

      {showAdd && (
        <div className="hm-smart-add-form">
          <input
            className="hm-smart-input"
            placeholder="Device name (e.g. Upstairs Thermostat)"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <select
            className="hm-smart-input"
            value={form.brand}
            onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
          >
            <option value="manual">Manual / local sensor</option>
            <option value="nest">Nest thermostat</option>
          </select>
          <select className="hm-smart-input" value={form.device_type} onChange={e => setForm(f => ({ ...f, device_type: e.target.value }))}>
            <option value="air_quality_sensor">Air-quality sensor</option><option value="leak_sensor">Water-leak sensor</option><option value="smoke_co_sensor">Smoke / CO detector</option><option value="power_meter">Power meter</option><option value="inverter">Solar inverter</option><option value="battery">Home battery</option><option value="door_sensor">Door sensor</option><option value="smart_lock">Smart lock</option><option value="garage_door">Garage door</option><option value="appliance">Appliance</option><option value="thermostat">Thermostat</option>
          </select>
          <input
            className="hm-smart-input"
            placeholder="Location (optional, e.g. Upstairs)"
            value={form.location}
            onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
          />
          {error && <div className="hm-smart-form-error">{error}</div>}
          <button className="hm-smart-save-btn" onClick={handleAdd} disabled={saving || !form.name}>
            {saving ? "Adding…" : "Add device"}
          </button>
        </div>
      )}

      {devices.length === 0 ? (
        <div className="hm-smart-empty">
          <div className="hm-smart-empty-icon">◎</div>
          <div className="hm-smart-empty-title">No devices yet</div>
          <p className="hm-smart-empty-desc">Connect via Integrations tab or add manually above.</p>
        </div>
      ) : (
        <div className="hm-smart-device-list">
          {devices.map(d => {
            const state = d.last_state || {};
            return (
              <div key={d.id} className="hm-smart-device-row">
                <div className="hm-smart-device-info">
                  <span
                    className="hm-smart-device-dot"
                    style={{ background: state.status === "online" ? "var(--hm-smart-online)" : "var(--hm-muted)" }}
                  />
                  <div>
                    <div className="hm-smart-device-name">{d.name}</div>
                    <div className="hm-smart-device-meta">
                      {BRAND_LABELS[d.brand] || d.brand} · {d.device_type}
                      {d.location && ` · ${d.location}`}
                    </div>
                  </div>
                </div>
                <div className="hm-smart-device-actions">
                  {d.brand === "manual" ? <button className="hm-smart-icon-btn" onClick={() => { setSnapshotDevice(d); setBridgeInfo(null); }} title="Record reading">＋</button> : <button
                    className="hm-smart-icon-btn"
                    onClick={() => onPoll(d.id)}
                    disabled={pollingId === d.id}
                    title="Poll now"
                  >
                    {pollingId === d.id ? "…" : "↺"}
                  </button>}
                  <button
                    className="hm-smart-icon-btn hm-smart-icon-btn-del"
                    onClick={() => handleDelete(d.id, d.name)}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {snapshotDevice && <div className="hm-smart-add-form hm-smart-snapshot-form">
        <strong>Record {snapshotDevice.name} reading</strong>
        <div className="hm-smart-snapshot-grid">
          {[['temperature_f','Temperature °F'],['humidity_pct','Humidity %'],['battery_pct','Battery %'],['co2_ppm','CO₂ ppm'],['pm25','PM2.5 µg/m³']].map(([key,label]) => <label key={key}>{label}<input className="hm-smart-input" inputMode="decimal" value={snapshot[key]} onChange={e => setSnapshot(s => ({...s,[key]:e.target.value}))} /></label>)}
        </div>
        <div className="hm-smart-snapshot-toggles">{[['leak_detected','Leak detected'],['smoke_detected','Smoke detected'],['co_detected','CO detected']].map(([key,label]) => <label key={key}><input type="checkbox" checked={snapshot[key]} onChange={e => setSnapshot(s => ({...s,[key]:e.target.checked}))} /> {label}</label>)}</div>
        <select className="hm-smart-input" value={snapshot.door_state} onChange={e => setSnapshot(s => ({...s,door_state:e.target.value}))}><option value="">No door state</option><option value="closed">Door closed</option><option value="open">Door open</option><option value="locked">Locked</option><option value="unlocked">Unlocked</option></select>
        {error && <div className="hm-smart-form-error">{error}</div>}
        <div className="hm-smart-equipment-actions"><button onClick={saveSnapshot} disabled={saving}>{saving ? "Saving…" : "Save reading"}</button><button className="is-secondary" onClick={() => setSnapshotDevice(null)}>Cancel</button></div>
        <div className="hm-smart-bridge-box"><strong>Local bridge</strong><p>Create a one-time token for Home Assistant, Raspberry Pi, or another trusted collector. Creating a new token revokes the old one.</p>{bridgeInfo ? <><code>{bridgeInfo.token}</code><small>POST JSON to {bridgeInfo.ingest_url} with header X-HomeMind-Token. Save this token now; HouseMind stores only its hash.</small></> : <button onClick={createBridgeToken} disabled={saving}>Create bridge token</button>}</div>
      </div>}
    </div>
  );
}

// ── Alerts tab ────────────────────────────────────────────────────────────────

function AlertsTab({ alerts, onRefresh }) {
  const [showDismissed, setShowDismissed] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const visible = showDismissed ? alerts : alerts.filter(a => !a.is_dismissed);

  const dismiss = async (id) => {
    try {
      await homeAiApi.dismissAlert(id);
      onRefresh();
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await homeAiApi.markAllRead();
      onRefresh();
    } catch {}
  };

  const toggleSelected = (id) => setSelected(current => {
    const next = new Set(current);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const removeAlerts = async (all = false) => {
    if (!all && selected.size === 0) return;
    const label = all ? "all HomeMind alerts" : `${selected.size} selected alert${selected.size === 1 ? "" : "s"}`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      if (all) await homeAiApi.deleteAllAlerts();
      else await homeAiApi.deleteAlerts([...selected]);
      setSelected(new Set());
      setSelecting(false);
      await onRefresh();
    } finally { setDeleting(false); }
  };

  return (
    <div className="hm-smart-alerts-tab">
      <div className="hm-smart-alerts-head">
        <div className="hm-smart-section-label" style={{ margin: 0 }}>
          🔔 Alerts {alerts.filter(a => !a.is_read && !a.is_dismissed).length > 0 &&
            <span className="hm-smart-unread-badge">
              {alerts.filter(a => !a.is_read && !a.is_dismissed).length}
            </span>}
        </div>
        <div className="hm-smart-alerts-actions">
          <button className="hm-smart-text-btn" onClick={markAllRead}>Mark all read</button>
          <button className="hm-smart-text-btn" onClick={() => { setSelecting(v => !v); setSelected(new Set()); }}>
            {selecting ? "Cancel" : "Select"}
          </button>
          <button className="hm-smart-text-btn" onClick={() => setShowDismissed(v => !v)}>
            {showDismissed ? "Hide dismissed" : "Show dismissed"}
          </button>
        </div>
      </div>

      {visible.length > 0 && (
        <div className={`hm-smart-alert-bulk${selecting ? " is-visible" : ""}`}>
          {selecting ? <>
            <button onClick={() => setSelected(new Set(visible.map(alert => alert.id)))}>Select all shown</button>
            <span>{selected.size} selected</span>
            <button className="is-danger" disabled={!selected.size || deleting} onClick={() => removeAlerts(false)}>Delete selected</button>
          </> : <button className="is-danger" disabled={deleting} onClick={() => removeAlerts(true)}>Delete all alerts</button>}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="hm-smart-empty">
          <div className="hm-smart-empty-icon">◉</div>
          <div className="hm-smart-empty-title">No alerts</div>
          <p className="hm-smart-empty-desc">Alerts appear here when your devices exceed thresholds.</p>
        </div>
      ) : (
        <div className="hm-smart-alert-list hm-smart-alert-list-full">
          {visible.map(a => (
            <div key={a.id} className={`hm-smart-alert-row${a.is_dismissed ? " hm-smart-alert-dismissed" : ""}${!a.is_read ? " hm-smart-alert-unread" : ""}`}>
              {selecting && <input className="hm-smart-alert-check" type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelected(a.id)} aria-label={`Select ${a.title}`} />}
              <span
                className="hm-smart-alert-dot"
                style={{ background: SEVERITY_COLOR[a.severity] || "var(--hm-muted)" }}
              />
              <div className="hm-smart-alert-body">
                <div className="hm-smart-alert-title">{a.title}</div>
                {a.message && <div className="hm-smart-alert-msg">{a.message}</div>}
                <div className="hm-smart-alert-time">
                  {new Date(a.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                  {a.device_name && ` · ${a.device_name}`}
                </div>
              </div>
              {!a.is_dismissed && (
                <button className="hm-smart-dismiss-btn" onClick={() => dismiss(a.id)} title="Dismiss">✕</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Integrations tab ──────────────────────────────────────────────────────────

const STATUS_BADGE = {
  available: { label: "Available",   color: "var(--hm-smart-online)" },
  built_in:  { label: "Active",      color: "var(--hm-smart-online)" },
  planned:   { label: "Phase",       color: "var(--hm-muted)" },
  paused:    { label: "Paused",      color: "var(--hm-muted)" },
  future:    { label: "Future",      color: "var(--hm-muted)" },
};

function IntegrationsTab({ integrations, onRefresh }) {
  const [connectingBrand, setConnectingBrand] = useState(null);
  const [homeAssistantForm, setHomeAssistantForm] = useState(null);

  const handleConnect = async (brand) => {
    if (brand === "home_assistant") {
      setHomeAssistantForm({ base_url: "", access_token: "" });
      return;
    }
    if (!["nest", "smartthings"].includes(brand)) return;
    setConnectingBrand(brand);
    try {
      const smartThings = brand === "smartthings";
      const redirectUri = `${window.location.origin}/app/smart-home?${smartThings ? "smartthings_callback" : "nest_callback"}=1`;
      const { auth_url } = smartThings
        ? await homeAiApi.getSmartThingsAuthUrl(redirectUri)
        : await homeAiApi.getNestAuthUrl(redirectUri);
      window.location.href = auth_url;
    } catch (e) {
      alert(e.message || "Could not start device authorization");
      setConnectingBrand(null);
    }
  };

  const connectHomeAssistant = async (event) => {
    event.preventDefault();
    setConnectingBrand("home_assistant");
    try {
      const result = await homeAiApi.connectHomeAssistant(homeAssistantForm);
      setHomeAssistantForm(null);
      onRefresh();
      alert(`Home Assistant connected · ${result.devices_connected} supported entities imported`);
    } catch (e) {
      alert(e.message || "Home Assistant connection failed");
    } finally {
      setConnectingBrand(null);
    }
  };

  const handleDisconnect = async (brand, name) => {
    if (!window.confirm(`Disconnect ${name}?`)) return;
    try {
      await homeAiApi.disconnectIntegration(brand);
      onRefresh();
    } catch (e) {
      alert(e.message || "Disconnect failed");
    }
  };

  return (
    <div className="hm-smart-integrations">
      <div className="hm-smart-section-label">🔗 Device integrations</div>
      <div className="hm-smart-int-grid">
        {integrations.map(i => {
          const badge = STATUS_BADGE[i.status] || STATUS_BADGE.planned;
          return (
            <div key={i.brand} className={`hm-smart-int-card${i.connected ? " hm-smart-int-connected" : ""}${i.status !== "available" ? " hm-smart-int-planned" : ""}`}>
              <div className="hm-smart-int-head">
                <div className="hm-smart-int-name">{i.name}</div>
                {i.connected ? (
                  <span className="hm-smart-int-badge" style={{ background: "var(--hm-smart-online-bg)", color: "var(--hm-smart-online)" }}>
                    Connected
                  </span>
                ) : (
                  <span className="hm-smart-int-badge" style={{ color: badge.color }}>
                    {badge.label}{i.status === "planned" && i.phase ? ` ${i.phase}` : ""}
                  </span>
                )}
              </div>
              <p className="hm-smart-int-desc">{i.description}</p>
              {i.connected ? (
                <button
                  className="hm-smart-disconnect-btn"
                  onClick={() => handleDisconnect(i.brand, i.name)}
                >
                  Disconnect
                </button>
              ) : i.status === "built_in" ? (
                <span className="hm-smart-coming-soon-label hm-smart-built-in-label">
                  Uses your HouseMind location
                </span>
              ) : i.can_connect ? (
                <button
                  className="hm-smart-connect-btn"
                  onClick={() => handleConnect(i.brand)}
                  disabled={connectingBrand === i.brand}
                >
                  {connectingBrand === i.brand ? "Redirecting…" : "Connect"}
                </button>
              ) : (
                <span className="hm-smart-coming-soon-label">Not yet available</span>
              )}
            </div>
          );
        })}
      </div>
      {homeAssistantForm && <div className="hm-smart-modal-backdrop" role="presentation" onMouseDown={() => setHomeAssistantForm(null)}>
        <form className="hm-smart-modal hm-smart-ha-form" onSubmit={connectHomeAssistant} onMouseDown={event => event.stopPropagation()}>
          <div className="hm-smart-section-label">🏡 Home Assistant bridge</div>
          <h3>Connect read-only monitoring</h3>
          <p>Use a remotely reachable Home Assistant URL and a long-lived access token. HouseMind encrypts the token and never displays it again.</p>
          <label><span>Home Assistant URL</span><input type="url" required placeholder="https://your-home.ui.nabu.casa" value={homeAssistantForm.base_url} onChange={event => setHomeAssistantForm({...homeAssistantForm, base_url: event.target.value})} /></label>
          <label><span>Long-lived access token</span><textarea required rows="4" autoComplete="off" value={homeAssistantForm.access_token} onChange={event => setHomeAssistantForm({...homeAssistantForm, access_token: event.target.value})} /></label>
          <small>Home Assistant → Profile → Security → Long-Lived Access Tokens. A local 192.168.x.x URL cannot be reached by Render.</small>
          <div className="hm-smart-modal-actions"><button type="button" onClick={() => setHomeAssistantForm(null)}>Cancel</button><button type="submit" disabled={connectingBrand === "home_assistant"}>{connectingBrand === "home_assistant" ? "Discovering…" : "Connect and discover"}</button></div>
        </form>
      </div>}
    </div>
  );
}

// ── Settings tab ──────────────────────────────────────────────────────────────

function pushApplicationKey(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const bytes = window.atob(base64);
  return Uint8Array.from(bytes, character => character.charCodeAt(0));
}

function PhoneNotificationsCard() {
  const supported = typeof window !== "undefined"
    && "Notification" in window
    && "serviceWorker" in navigator
    && "PushManager" in window;
  const standalone = typeof window !== "undefined"
    && (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true);
  const [config, setConfig] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!supported) return;
    notificationApi.pushConfig()
      .then(async result => {
        setConfig(result);
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setEnabled(Boolean(subscription && result.subscribed));
      })
      .catch(e => setMessage(e.message || "Could not load phone notification settings"));
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    setMessage("");
    try {
      if (!config?.configured) throw new Error("Phone notification keys are not configured yet.");
      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("Notification permission was not granted.");
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: pushApplicationKey(config.public_key),
        });
      }
      await notificationApi.subscribePush({
        ...subscription.toJSON(),
        user_agent: navigator.userAgent,
      });
      setEnabled(true);
      const test = await notificationApi.testPush();
      setMessage(test?.sent ? "Phone notifications are on — test sent" : "Phone notifications are on");
    } catch (e) {
      setMessage(e.message || "Could not enable phone notifications");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setMessage("");
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      await notificationApi.unsubscribePush(subscription?.endpoint || "");
      if (subscription) await subscription.unsubscribe();
      setEnabled(false);
      setMessage("Phone notifications are off");
    } catch (e) {
      setMessage(e.message || "Could not disable phone notifications");
    } finally {
      setBusy(false);
    }
  };

  const testPhone = async () => {
    setBusy(true);
    setMessage("");
    try {
      const result = await notificationApi.testPush();
      setMessage(
        result?.sent
          ? "Test accepted by your phone push service"
          : "The phone push service did not accept the test"
      );
    } catch (e) {
      setMessage(
        `${e.message || "Could not send the phone test"}. If this repeats, turn phone notifications off and enable them again.`
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hm-smart-push-card">
      <div className="hm-smart-push-icon">↗</div>
      <div className="hm-smart-push-copy">
        <div className="hm-smart-push-kicker">📲 Phone delivery</div>
        <strong>{enabled ? "Push notifications are on" : "Get HomeMind alerts on your phone"}</strong>
        <p>
          HomeMind will send the same important HVAC alerts that appear in the HouseMind bell.
        </p>
        {!supported && <small>This browser does not support Web Push.</small>}
        {supported && !standalone && /iPhone|iPad|iPod/i.test(navigator.userAgent) && (
          <small>On iPhone or iPad, add HouseMind to your Home Screen first, then open the installed app.</small>
        )}
        {message && <small className={enabled ? "is-success" : ""}>{message}</small>}
      </div>
      {supported && (
        <div className="hm-smart-push-actions">
          {enabled && (
            <button type="button" onClick={testPhone} disabled={busy || !config}>
              {busy ? "Testing…" : "Test phone"}
            </button>
          )}
          <button type="button" onClick={enabled ? disable : enable} disabled={busy || !config}>
            {busy ? "Working…" : enabled ? "Turn off" : "Enable"}
          </button>
        </div>
      )}
    </div>
  );
}

function SettingsTab({ devices }) {
  const thermostats = devices.filter(device => device.device_type === "thermostat");
  const toastActions = useToastActions();
  const [selectedId, setSelectedId] = useState(thermostats[0]?.id || "");
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  useEffect(() => {
    if (!selectedId) {
      setSettings(null);
      return;
    }
    setBusy(true);
    setMessage("");
    homeAiApi.getDeviceSettings(selectedId)
      .then(result => {
        setSettings(result);
        setDirty(false);
        const stored = window.localStorage.getItem(`homemind-settings-saved-${selectedId}`);
        setLastSavedAt(stored ? new Date(stored) : null);
      })
      .catch(e => setMessage(e.message || "Could not load settings"))
      .finally(() => setBusy(false));
  }, [selectedId]);

  const update = (field, value) => {
    setSettings(current => ({ ...current, [field]: value }));
    setDirty(true);
    setMessage("Unsaved changes");
  };
  const save = async () => {
    const deviceName = thermostats.find(device => device.id === Number(selectedId))?.name || "thermostat";
    const toastId = toastActions?.addToast?.(`Saving ${deviceName} settings…`, "loading", { persist: true });
    setBusy(true);
    setMessage("");
    try {
      const saved = await homeAiApi.updateDeviceSettings(selectedId, settings);
      setSettings(saved);
      const savedAt = new Date();
      window.localStorage.setItem(`homemind-settings-saved-${selectedId}`, savedAt.toISOString());
      setLastSavedAt(savedAt);
      setDirty(false);
      setMessage("All changes saved");
      toastActions?.updateToast?.(toastId, `${deviceName} settings saved`, "success");
    } catch (e) {
      const error = e.message || "Could not save settings";
      setMessage(error);
      toastActions?.updateToast?.(toastId, error, "error");
    } finally {
      setBusy(false);
    }
  };

  if (!thermostats.length) {
    return (
      <div className="hm-smart-settings">
        <PhoneNotificationsCard />
        <div className="hm-smart-empty">
          <div className="hm-smart-empty-icon">⚙</div>
          <div className="hm-smart-empty-title">Connect a thermostat first</div>
          <p className="hm-smart-empty-desc">Device-specific monitoring controls will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hm-smart-settings">
      <PhoneNotificationsCard />
      <div className="hm-smart-settings-head">
        <div>
          <div className="hm-smart-section-label">⚙️ Monitoring controls</div>
          <p>Choose when HomeMind polls, alerts, and sends a bell notification.</p>
        </div>
        <select
          className="hm-smart-input"
          value={selectedId}
          onChange={e => setSelectedId(Number(e.target.value))}
        >
          {thermostats.map(device => <option key={device.id} value={device.id}>{device.name}</option>)}
        </select>
      </div>

      {settings && (
        <div className="hm-smart-settings-card">
          <label className="hm-smart-setting-row">
            <span><strong>Polling interval</strong><small>How often HomeMind requests fresh device data.</small></span>
            <select value={settings.poll_interval_min} onChange={e => update("poll_interval_min", Number(e.target.value))}>
              {[5, 10, 15, 30, 60].map(value => <option key={value} value={value}>{value} min</option>)}
            </select>
          </label>
          <label className="hm-smart-setting-row">
            <span><strong>Early usage warning</strong><small>Send a first phone and bell warning during an extended cycle.</small></span>
            <input type="number" min="15" max="120" value={settings.early_warning_min} onChange={e => update("early_warning_min", Number(e.target.value))} />
            <em>min</em>
          </label>
          <label className="hm-smart-setting-row">
            <span><strong>Long runtime alert</strong><small>Alert after continuous heating or cooling.</small></span>
            <input type="number" min="15" max="240" value={settings.runtime_threshold_min} onChange={e => update("runtime_threshold_min", Number(e.target.value))} />
            <em>min</em>
          </label>
          <label className="hm-smart-setting-row">
            <span><strong>Expected temperature change</strong><small>Moderate-weather baseline. HomeMind adjusts this for outdoor load, humidity, and proximity to setpoint.</small></span>
            <input type="number" min="0.5" max="10" step="0.5" value={settings.temperature_delta_f} onChange={e => update("temperature_delta_f", Number(e.target.value))} />
            <em>°F</em>
          </label>
          <label className="hm-smart-setting-row">
            <span><strong>Temperature window</strong><small>Time allowed to reach the expected change.</small></span>
            <input type="number" min="15" max="180" step="5" value={settings.temperature_window_min} onChange={e => update("temperature_window_min", Number(e.target.value))} />
            <em>min</em>
          </label>
          <label className="hm-smart-setting-row hm-smart-setting-toggle">
            <span><strong>HouseMind bell notifications</strong><small>Send generated HomeMind alerts to the existing bell.</small></span>
            <input type="checkbox" checked={settings.notifications_enabled} onChange={e => update("notifications_enabled", e.target.checked)} />
          </label>
          <div className="hm-smart-settings-save">
            <span className={!dirty && lastSavedAt ? "is-success" : ""}>
              {dirty
                ? "Unsaved changes"
                : lastSavedAt
                  ? `Last saved ${lastSavedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                  : message || "Settings loaded"}
            </span>
            <button onClick={save} disabled={busy || !dirty}>
              {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </button>
          </div>
        </div>
      )}
      {busy && !settings && <div className="hm-smart-loading">Loading monitoring settings…</div>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SmartHomePage() {
  const toastActions = useToastActions();
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get("tab");
    return ["dashboard", "devices", "alerts", "analytics", "ai", "integrations", "settings"].includes(requested)
      ? requested
      : "dashboard";
  });
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [weather, setWeather] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [energy, setEnergy] = useState(null);
  const [applianceHealth, setApplianceHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollingId, setPollingId] = useState(null);

  // Handle Nest and SmartThings OAuth callbacks
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const smartThingsStatus = params.get("smartthings_status");
    if (smartThingsStatus) {
      window.history.replaceState({}, "", window.location.pathname);
      if (smartThingsStatus === "connected") {
        toastActions.addToast(`SmartThings connected${params.get("devices") ? ` · ${params.get("devices")} devices found` : ""}`, "success");
        loadAll();
      } else {
        toastActions.addToast("SmartThings connection could not be completed. Please try again.", "error");
      }
      return;
    }
    const code = params.get("code");
    const callbackFlag = params.get("nest_callback");
    const smartThingsFlag = params.get("smartthings_callback");
    if (code && smartThingsFlag === "1") {
      const redirectUri = `${window.location.origin}/app/smart-home?smartthings_callback=1`;
      homeAiApi.smartThingsCallback(code, params.get("state"), redirectUri)
        .then(() => {
          window.history.replaceState({}, "", window.location.pathname);
          loadAll();
        })
        .catch(e => alert(`SmartThings connection failed: ${e.message}`));
      return;
    }
    if (code && callbackFlag === "1") {
      const redirectUri = `${window.location.origin}/app/smart-home?nest_callback=1`;
      homeAiApi.nestCallback(code, redirectUri)
        .then(() => {
          // Clean URL and reload
          window.history.replaceState({}, "", window.location.pathname);
          loadAll();
        })
        .catch(e => alert(`Nest connection failed: ${e.message}`));
    }
  }, []); // eslint-disable-line

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [devs, alts, ints, outside, insight, energyInsight, applianceInsight] = await Promise.allSettled([
        homeAiApi.listDevices(),
        homeAiApi.listAlerts(),
        homeAiApi.listIntegrations(),
        homeAiApi.currentWeather(),
        homeAiApi.analysis(),
        homeAiApi.energySummary(),
        homeAiApi.applianceHealth(),
      ]);
      setDevices(devs.status === "fulfilled" ? devs.value : []);
      setAlerts(alts.status === "fulfilled" ? alts.value : []);
      setIntegrations(ints.status === "fulfilled" ? ints.value : []);
      setWeather(outside.status === "fulfilled" ? outside.value : null);
      setAnalysis(insight.status === "fulfilled" ? insight.value : null);
      setEnergy(energyInsight.status === "fulfilled" ? energyInsight.value : null);
      setApplianceHealth(applianceInsight.status === "fulfilled" ? applianceInsight.value : null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handlePoll = async (deviceId) => {
    const deviceName = devices.find(device => device.id === deviceId)?.name || "Thermostat";
    const toastId = toastActions?.addToast?.(`Refreshing ${deviceName}…`, "loading", { persist: true });
    setPollingId(deviceId);
    try {
      const result = await homeAiApi.pollDevice(deviceId);
      const [devs, alts, insight, energyInsight] = await Promise.allSettled([
        homeAiApi.listDevices(),
        homeAiApi.listAlerts(),
        homeAiApi.analysis(),
        homeAiApi.energySummary(),
      ]);
      if (devs.status === "fulfilled") setDevices(devs.value);
      if (alts.status === "fulfilled") setAlerts(alts.value);
      if (insight.status === "fulfilled") setAnalysis(insight.value);
      if (energyInsight.status === "fulfilled") setEnergy(energyInsight.value);
      toastActions?.updateToast?.(
        toastId,
        result?.warning || `${deviceName} refreshed with the latest Nest reading`,
        result?.warning ? "info" : "success",
      );
    } catch (e) {
      toastActions?.updateToast?.(
        toastId,
        e.message || `${deviceName} could not be refreshed`,
        "error",
      );
    } finally {
      setPollingId(null);
    }
  };

  const unreadCount = alerts.filter(a => !a.is_read && !a.is_dismissed).length;

  return (
    <div className="hm-smart-page">
      {/* Page header */}
      <div className="hm-smart-header">
        <div className="hm-smart-header-copy">
          <div className="hm-smart-header-kicker">🔮 Future home</div>
          <div className="hm-smart-header-title">
            <span className="hm-smart-header-icon">⬡</span>
            Smart Home AI
            <span className="hm-smart-header-admin">Preview</span>
          </div>
        </div>
        <button className="hm-smart-refresh-btn" onClick={loadAll} disabled={loading}>
          {loading ? "…" : "↺"}
        </button>
      </div>

      {/* Tab nav */}
      <div className="hm-smart-tabnav">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`hm-smart-tab${activeTab === tab.id ? " is-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="hm-smart-tab-icon">{tab.icon}</span>
            <span className="hm-smart-tab-label">{tab.label}</span>
            {tab.id === "alerts" && unreadCount > 0 && (
              <span className="hm-smart-tab-badge">{unreadCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="hm-smart-content">
        {loading ? (
          <div className="hm-smart-loading">Loading Smart Home AI…</div>
        ) : (
          <>
            {activeTab === "dashboard" && (
              <DashboardTab
                devices={devices}
                alerts={alerts}
                weather={weather}
                analysis={analysis}
                energy={energy}
                applianceHealth={applianceHealth}
                onPoll={handlePoll}
                pollingId={pollingId}
                onOpenIntegrations={() => setActiveTab("integrations")}
                onOpenAi={() => setActiveTab("ai")}
                onRefresh={loadAll}
              />
            )}
            {activeTab === "devices" && (
              <DevicesTab
                devices={devices}
                onPoll={handlePoll}
                pollingId={pollingId}
                onRefresh={loadAll}
              />
            )}
            {activeTab === "alerts" && (
              <AlertsTab alerts={alerts} onRefresh={loadAll} />
            )}
            {activeTab === "analytics" && (
              <EnergyIntelligence energy={energy} />
            )}
            {activeTab === "ai" && (
              <AiAssistantTab analysis={analysis} />
            )}
            {activeTab === "integrations" && (
              <IntegrationsTab integrations={integrations} onRefresh={loadAll} />
            )}
            {activeTab === "settings" && (
              <SettingsTab devices={devices} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
